import { ChangeDetectionStrategy, Component, computed, input, model, Signal } from '@angular/core';
import { DsMessageModule } from '@bmw-ds/components';

import { I18nInterpolateDirective } from '../../../i18n/components/i18n-interpolate.directive';
import { I18nComponent } from '../../../i18n/components/i18n.component';
import { useI18n } from '../../../i18n/services/i18n.service';
import { LoadingIndicatorComponent } from '../../../loading/loading-indicator.component';
import { ConsentCheckboxGroup, ConsentMetadata, ConsentSelection, ConsentType } from '../../model/consent-checkbox';
import { ConsentLegalText } from '../../model/nsc-consent-legal-text';
import { ConsentDataComponent } from '../consent-data/consent-data.component';
import { GcdmLanguageContainer } from '../consent-data/gcdm-language.container';
import { ConsentPickerComponent } from '../consent-picker/consent-picker.component';
import { LegalExpandableComponent } from '../legal-text/legal-expandable.component';

@Component({
  selector: 'cdf-consent-box-content',
  imports: [
    LegalExpandableComponent,
    ConsentPickerComponent,
    LoadingIndicatorComponent,
    GcdmLanguageContainer,
    DsMessageModule,
    I18nComponent,
    I18nInterpolateDirective,
    I18nComponent,
    I18nInterpolateDirective,
    ConsentDataComponent,
  ],
  template: `
    @if (consentCheckboxGroups(); as consentCheckboxGroups) {
      @if (consentSelection(); as selection) {
        <div class="d-flex flex-column gap-4x">
          <!-- head title -->
          @switch (consentType()) {
            @case (ConsentType.DEALER) {
              <h2>{{ i18n('consent.dealerHeader') }}</h2>
            }
            @case (ConsentType.NSC) {
              <h2>{{ i18n('consent.nscHeader') }}</h2>
            }
          }

          <!-- consent date -->
          <div class="d-flex">
            @if (consentSaveDate(); as consentDate) {
              <i18n key="consent.lastSaved">
                <time *i18nInterpolate="'date'" [dateTime]="consentDate">{{ i18n.date(consentDate) }}</time>
              </i18n>
            } @else {
              {{ i18n('consent.neverSaved') }}
            }
          </div>

          <!-- consent version and language picker -->
          @if (consentType() === ConsentType.NSC) {
            <cdf-consent-data class="d-flex" [metadata]="consentMetadata()" />

            <cdf-gcdm-language-container
              class="d-flex"
              [label]="i18n('consent.language')"
              [correspondenceLanguage]="customerLanguage()"
            />
          }

          <!-- general information text -->
          @if (legalText().header; as header) {
            <div class="d-flex">
              <cdf-legal-expandable [rootLegalText]="header" />
            </div>
          }

          <!-- consent legal prefix text and detailed tooltip -->
          @if (legalText().beforeUsageDef; as beforeUsageDef) {
            <cdf-legal-expandable [rootLegalText]="beforeUsageDef" />
          }
          @if (readonlyTooltip()) {
            <ds-message [messageText]="readonlyTooltip()" tone="caution" />
          }

          <!-- banner there is no consent existing -->
          @if (initialConsentCreationMode()) {
            <ds-message [messageText]="i18n('consent.initialConsentCreationMode')" tone="info" />
          }

          <!-- consent checkboxes -->
          <cdf-consent-picker
            [consentCheckboxGroups]="consentCheckboxGroups"
            [consentSelection]="selection"
            [disabled]="areCheckboxesDisabled()"
            [readonlyTooltip]="readonlyTooltip()"
            [initialConsentCreationMode]="initialConsentCreationMode()"
            (consentSelectionChange)="consentSelection.set($event)"
            disabledExceptionUsageId="dataProcessing"
          />

          <!-- error due to wrong checkbox selection -->
          @if (onlyImplicitConsentsSelected()) {
            <ds-message [messageText]="onlyImplicitConsentsSelectedMessage()" tone="caution" />
          }

          <!-- consent legal suffix text -->
          @if (legalText().afterUsageDef; as afterUsageDef) {
            <cdf-legal-expandable [rootLegalText]="afterUsageDef" />
          }
        </div>

        <ng-content />
      } @else {
        <cdf-loading-indicator>{{ i18n('consent.loadingExisting') }}</cdf-loading-indicator>
      }
    } @else {
      <cdf-loading-indicator />
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConsentBoxContentComponent {
  protected readonly i18n = useI18n();

  readonly customerLanguage = input.required<string | undefined>();
  readonly consentSelection = model.required<ConsentSelection | undefined>();
  readonly consentCheckboxGroups = input.required<ConsentCheckboxGroup[] | undefined>();
  readonly legalText = input.required<ConsentLegalText>();
  readonly onlyImplicitConsentsSelected = input(false);
  readonly initialConsentCreationMode = input(false);
  readonly consentType = input<ConsentType>(ConsentType.NSC);
  readonly consentSaveDate = input<Date>();
  readonly consentMetadata = input<ConsentMetadata>();
  readonly readonlyTooltip = input('');

  readonly onlyImplicitConsentsSelectedMessage = computed(() => {
    const consentSelectionLabels = this.consentSelectionLabels();
    return this.i18n('consent.onlyImplicitConsentsSelected', {
      count: consentSelectionLabels.length,
      list: consentSelectionLabels.join(', '),
    });
  });

  readonly areCheckboxesDisabled: Signal<boolean> = computed(() => {
    const consentSelection = this.consentSelection();
    if (!consentSelection) {
      return false;
    }

    return (
      this.consentType() === ConsentType.DEALER &&
      'dataProcessing' in consentSelection &&
      !consentSelection['dataProcessing']
    );
  });

  readonly consentSelectionLabels = computed(() => {
    const consentSelection = this.consentSelection();
    const consentCheckboxGroups = this.consentCheckboxGroups();
    if (!consentSelection || Object.values(consentSelection).length === 0 || !consentCheckboxGroups) {
      return [];
    }

    // Return all consent checkbox labels which are selected
    return Object.entries(consentSelection)
      .filter(([_key, isSelected]) => isSelected)
      .flatMap(([key, _isSelected]) =>
        consentCheckboxGroups.flatMap((consentCheckbox) => {
          const selectedCheckboxes = consentCheckbox.checkboxes.filter((checkbox) => checkbox.key === key);
          return selectedCheckboxes.map((checkbox) => checkbox.label);
        }),
      );
  });

  protected readonly ConsentType = ConsentType;
}
